import { useEffect, type FormEvent } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm } from "../useForm";
import InputTextOnly from "../../controls/InputTextOnly";
import SelectMenu from "../../controls/SelectMenu";
import { convertToDefEventPara } from "../../../shared/utility";
import type { TaxRate } from "../../../types/taxRates";

interface StatusOption {
  value: boolean;
  label: string;
}

interface TaxRateFormValues {
  name: string;
  rate: string;
  active: boolean;
}

interface SaveResult {
  success: boolean;
  message?: string;
}

interface TaxRateFormProps {
  taxRate: TaxRate | null;
  onSave: (values: TaxRateFormValues, id?: number) => Promise<SaveResult>;
  onCancel: () => void;
}

const statusOptions: StatusOption[] = [
  { value: true, label: "Active" },
  { value: false, label: "Inactive" },
];

const initialFValues = {
  name: "",
  rate: "",
  active: true,
};

export default function TaxRateForm({ taxRate, onSave, onCancel }: TaxRateFormProps) {
  const isEdit = Boolean(taxRate);
  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false
  );

  useEffect(() => {
    if (taxRate) {
      setValues({
        name: taxRate.name ?? "",
        rate: taxRate.rate != null ? String(Number(taxRate.rate)) : "",
        active: Boolean(taxRate.active),
      });
    } else {
      setValues(initialFValues);
    }
  }, [taxRate, setValues]);

  const validate = (fieldValues: TaxRateFormValues = values) => {
    let temp = { ...errors };
    const rate = Number(fieldValues.rate);

    delete temp.name;
    if (!fieldValues.name?.trim()) {
      temp.name = "Please fill out field.";
    }

    delete temp.rate;
    if (fieldValues.rate === "" || !Number.isFinite(rate) || rate < 0 || rate > 100) {
      temp.rate = "Enter a percentage between 0 and 100.";
    }

    setErrors(temp);

    if (fieldValues === values) {
      return Object.keys(temp).length === 0;
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    const result = await onSave(values, taxRate?.id);
    if (!result.success) {
      setErrors((prev) => ({
        ...prev,
        name: result.message || "Could not save tax rate.",
      }));
    }
  };

  const selectedStatus =
    statusOptions.find((option) => option.value === Boolean(values.active)) || statusOptions[0];

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col bg-white">
      <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? "Edit tax rate" : "Add tax rate"}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Used as a default for taxable customer order items.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-100"
          aria-label="Close panel"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-4 max-w-4xl">
            <div>
              <InputTextOnly
                label="Name"
                name="name"
                onChange={handleInputChange}
                value={values.name}
                labelOn={true}
                error={errors.name ?? undefined}
                placeholder="HST Ontario"
              />
            </div>
            <div>
              <InputTextOnly
                label="Rate (%)"
                name="rate"
                type="number"
                onChange={handleInputChange}
                value={values.rate}
                labelOn={true}
                error={errors.rate ?? undefined}
                min="0"
                max="100"
                step="0.0001"
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={(e) =>
                  handleInputChange(convertToDefEventPara("active", e.target.value?.value ?? true))
                }
                name="active"
                value={selectedStatus}
                options={statusOptions}
                title="Status"
              />
            </div>
          </div>

          <div className="sm:flex-no-wrap">
            <div className="my-auto flex items-center justify-between w-full">
              <div>
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center mt-3"
                >
                  {isEdit ? "Save" : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
